import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useRestaurantId } from "@/hooks/useRestaurantId";
import type { Json } from "@/integrations/supabase/types";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  icon: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
  metadata: Json;
}

export function useNotifications() {
  const restaurantId = useRestaurantId();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("created_at", { ascending: false })
      .limit(50);
    
    if (!error && data) {
      setNotifications(data);
      setUnreadCount(data.filter((n: Notification) => !n.is_read).length);
    }
    setLoading(false);
  }, [restaurantId]);

  const markAsRead = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", id)
        .eq("restaurant_id", restaurantId);

      if (!error) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    },
    [restaurantId]
  );

  const markAllAsRead = useCallback(async () => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("is_read", false)
      .eq("restaurant_id", restaurantId);

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  }, [restaurantId]);

  const deleteNotification = useCallback(
    async (id: string) => {
      const n = notifications.find((n) => n.id === id);
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", id)
        .eq("restaurant_id", restaurantId);
      if (!error) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        if (n && !n.is_read) setUnreadCount((c) => Math.max(0, c - 1));
      }
    },
    [notifications, restaurantId]
  );

  const clearAll = useCallback(async () => {
    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("restaurant_id", restaurantId)
      .neq("id", "00000000-0000-0000-0000-000000000000");
    if (!error) {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchNotifications();

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => {
          const newNotif = payload.new as Notification;
          setNotifications((prev) => [newNotif, ...prev].slice(0, 50));
          setUnreadCount((c) => c + 1);

          // Show sonner toast for new notification
          toast.success(newNotif.title, {
            description: newNotif.message,
            duration: 5000,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications, restaurantId]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refetch: fetchNotifications,
  };
}
